import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useActions } from 'hooks/useActions';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import { SearchSelect } from 'components/SearchSelect/SearchSelect';
import Path from 'routes/Path';
import { userRoles } from 'constants/users';
import * as actions from './actions';
import * as authActions from 'global/actions/authActions';
import TicketsTable from './components/table';
import ExportXLSDropdown from './components/exportXLSDropdown';
import {
  TopHeadingContainer,
  ButtonsContainer,
  Header,
  AnchorLink,
  ITag,
  CompanyAndDepartmentDropDownContainer,
} from './styles';

const viewAllCompanies = 'All Companies';
const viewAllDepartments = 'All Departments';

export default function TicketsPage() {
  const { user, userRole, company } = useSelector((state) => state.auth);
  const {
    hasCandidateFeedbacks,
    departments,
    companies,
    tickets,
    isDepartmentOptionUpdated,
    isCompanyOptionUpdated,
    testsAvailableForCompany,
    archivedTicketId,
    loading,
  } = useSelector((state) => state.TicketsPageReducer);

  const {
    loadTickets,
    updateAdminDepartment,
    updateViewAllDepartments,
    updateViewAllCompanies,
    updateAdminCompany,
    resetUpdatedIds,
  } = useActions(actions);
  const { logout, verifyAuthState } = useActions(authActions);

  const [departmentSelect, setDepartmentSelect] = useState([]);
  const [companySelect, setCompanySelect] = useState([]);

  useEffect(() => {
    if (!userRole || !departments) return;
    setDepartmentSelect([
      {
        label: viewAllDepartments,
        value: viewAllDepartments,
        // icon: <CheckboxMark checked={user?.viewAllDepartments} />,
        icon: <i className={`fa-regular fa-square${user?.viewAllDepartments ? '-check' : ''} mr-1`} />,
      },
      ...departments,
    ]);
  }, [departments, userRole, user?.viewAllDepartments]);

  useEffect(() => {
    if (!userRole || !companies) return;
    setCompanySelect([
      {
        label: viewAllCompanies,
        value: viewAllCompanies,
        // icon: <CheckboxMark checked={user?.viewAll} />,
        icon: <i className={`fa-regular fa-square${user?.viewAll ? '-check' : ''} mr-1`} />,
      },
      ...companies,
    ]);
  }, [companies, userRole, user?.viewAll]);

  const handleUpdateAdminCompany = (response) => {
    if (response.label === viewAllCompanies) return updateViewAllCompanies({ viewAll: !user?.viewAll });
    updateAdminCompany(response._id);
  };

  const handleUpdateAdminDepartment = (response) => {
    if (response.label === viewAllDepartments) {
      // department dropdown remain with view all enabled so you can select department that tests are sent from
      if (user?.viewAll) return toast.error('View all companies enabled');
      return updateViewAllDepartments({ viewAllDepartments: !user?.viewAllDepartments });
    }
    updateAdminDepartment(response._id);
  };

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (isDepartmentOptionUpdated || isCompanyOptionUpdated) {
      verifyAuthState();
      loadTickets();
    }
  }, [isDepartmentOptionUpdated, isCompanyOptionUpdated, loadTickets, verifyAuthState]);

  useEffect(() => {
    if (archivedTicketId) {
      toast.success('Ticket archived successfully');
      verifyAuthState();
      resetUpdatedIds();
    }
  }, [archivedTicketId, resetUpdatedIds, verifyAuthState]);

  const renderButtons = () => (
    <ButtonsContainer>
      <ExportXLSDropdown tickets={tickets} />
      {hasCandidateFeedbacks && (
        <AnchorLink href={Path.candidateFeedbackPage}>
          <Button small primaryBlue primaryGreenBorder>
            <ITag className="fa-regular fa-comments" />
            Candidate Feedback
          </Button>
        </AnchorLink>
      )}
      <AnchorLink
        href={userRole === userRoles.DEPARTMENT_ADMIN ? `/department-settings/${user?.department}` : Path.settings}
      >
        <Button small primaryBlue primaryGreenBorder>
          <ITag className="fa fa-cog" />
          Settings
        </Button>
      </AnchorLink>

      {user.role === userRoles.SUPER_ADMIN && (
        <AnchorLink href={Path.superadmin}>
          <Button small primaryBlue primaryGreenBorder>
            <ITag className="fa fa-graduation-cap" />
            SuperAdmin
          </Button>
        </AnchorLink>
      )}
      {user.role !== userRoles.CANDIDATE && (
        <AnchorLink href="/?no-redirect=true">
          <Button small primaryBlue primaryGreenBorder>
            <ITag className="fa fa-home" />
            FrontPage
          </Button>
        </AnchorLink>
      )}
      <Button small primaryRed removeMargin onClick={() => logout()}>
        <ITag className="fa fa-sign-out" />
        Sign Out
      </Button>
    </ButtonsContainer>
  );

  const renderDepartmentDropdown = () => {
    return (
      <SearchSelect
        isDisabled={loading}
        options={departmentSelect}
        isClearable={false}
        isSearchable={true}
        placeholder="Department"
        width="200px"
        value={
          user.viewAllDepartments
            ? {
                label: viewAllDepartments,
                value: viewAllDepartments,
              }
            : departmentSelect.filter((d) => String(d._id) === String(user.adminDepartment ?? user.department))?.[0]
        }
        onChange={handleUpdateAdminDepartment}
      />
    );
  };

  const renderCompanyDropdown = () => {
    return (
      <CompanyAndDepartmentDropDownContainer>
        {departmentSelect.length > 2 && renderDepartmentDropdown()}
        <SearchSelect
          isDisabled={loading}
          options={companySelect}
          isClearable={false}
          isSearchable={true}
          placeholder="Company"
          width="250px"
          value={
            user.viewAll
              ? {
                  label: viewAllCompanies,
                  value: viewAllCompanies,
                }
              : companies.filter((c) => String(c._id) === String(user.adminCompany ?? user.company))?.[0]
          }
          onChange={handleUpdateAdminCompany}
        />
      </CompanyAndDepartmentDropDownContainer>
    );
  };

  return (
    <Wrapper padding="25px 0 0 0" minHeight="100vh">
      <Container full column>
        <TopHeadingContainer>
          <Header>Tests</Header>
          {renderButtons()}
        </TopHeadingContainer>
        {userRole === userRoles.COMPANY_ADMIN && company.testsOnly && departmentSelect.length > 2 && (
          <CompanyAndDepartmentDropDownContainer>{renderDepartmentDropdown()}</CompanyAndDepartmentDropDownContainer>
        )}
        {[userRoles.SUPER_ADMIN, userRoles.RESELLER].includes(userRole) && renderCompanyDropdown()}

        <TicketsTable
          tickets={JSON.parse(JSON.stringify(tickets))}
          user={user}
          loading={loading}
          testsAvailableForCompany={testsAvailableForCompany}
        />
      </Container>
    </Wrapper>
  );
}
